import { X, CheckCircle, AlertCircle, TrendingUp, Award, Target, Star } from 'lucide-react';

export default function LinkedInScoreModal({ candidate, job, onClose }) {
  if (!candidate || !job) return null;

  const scoreData = candidate.exa_score_breakdown || {};
  const score = candidate.relevance_score || 0;
  const structured = candidate.exa_structured || {};
  const sourcedData = candidate.sourced_data || {};
  
  const jobSkills = job.parsed_skills || [];
  const candidateSkills = structured.primary_skills || sourcedData.skills || [];
  
  // Calculate matches and gaps
  const matchedSkills = jobSkills.filter(skill => 
    candidateSkills.some(cSkill => cSkill.toLowerCase().includes(skill.toLowerCase()))
  );
  const gapSkills = jobSkills.filter(skill => 
    !candidateSkills.some(cSkill => cSkill.toLowerCase().includes(skill.toLowerCase()))
  );

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-[#2563eb]';
    if (score >= 30) return 'text-amber-600';
    return 'text-slate-400';
  };

  const getGradeBg = (score) => {
    if (score >= 70) return 'bg-green-50 text-green-700 border-green-200';
    if (score >= 50) return 'bg-blue-50 text-[#2563eb] border-blue-200';
    if (score >= 30) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-500 border-slate-200';
  };

  const getGrade = (score) => {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Needs Review';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ fontFamily: 'Georgia, serif' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="linkedin-score-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e40af] to-[#3b82f6] p-6 text-white relative flex-shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">LinkedIn Profile Analysis</h2>
              <p className="text-blue-100 text-sm">{candidate.name} vs {job.title}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Overall Score */}
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-slate-600 mb-2">Overall Match Score</p>
                <div className="flex items-baseline gap-3">
                  <span className={`text-5xl font-bold ${getScoreColor(score)}`}>{score}</span>
                  <span className="text-xl text-slate-400">/100</span>
                  <span className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${getGradeBg(score)}`}>
                    {getGrade(score)}
                  </span>
                </div>
              </div>
              {scoreData.skills != null && (
                <div className="text-right">
                  <p className="text-xs text-slate-500 mb-2">Score Breakdown</p>
                  <div className="space-y-1 text-xs">
                    <p className="text-slate-700">Skills: <span className="font-semibold">{scoreData.skills || 0}</span>/85</p>
                    <p className="text-slate-700">Experience: <span className="font-semibold">{scoreData.experience || 0}</span>/10</p>
                    <p className="text-slate-700">Seniority: <span className="font-semibold">{scoreData.seniority || 0}</span>/5</p>
                  </div>
                </div>
              )}
            </div>
            {scoreData.summary && (
              <p className="text-sm text-slate-600 leading-relaxed italic border-t border-slate-200 pt-3 mt-3">
                "{scoreData.summary}"
              </p>
            )}
          </div>

          {/* Skills Match */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-bold text-slate-800">
                Matching Skills <span className="text-slate-500 font-normal text-base">({matchedSkills.length}/{jobSkills.length})</span>
              </h3>
            </div>
            {matchedSkills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {matchedSkills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200">
                    ✓ {skill}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No clear skill matches found in profile</p>
            )}
          </div>

          {/* Gap Skills */}
          {gapSkills.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-bold text-slate-800">
                  Skills to Validate <span className="text-slate-500 font-normal text-base">({gapSkills.length})</span>
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {gapSkills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200">
                    ? {skill}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">May have these skills - review LinkedIn profile to confirm</p>
            </div>
          )}

          {/* Experience & Seniority */}
          <div className="grid grid-cols-2 gap-4">
            {(structured.years_experience || sourcedData.years_experience) && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-slate-400" />
                  <p className="text-xs font-semibold text-slate-600 uppercase">Experience</p>
                </div>
                <p className="text-2xl font-bold text-slate-800">
                  {structured.years_experience || sourcedData.years_experience} 
                  <span className="text-sm font-normal text-slate-500">years</span>
                </p>
              </div>
            )}
            {(structured.seniority_level || sourcedData.seniority_level) && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                  <p className="text-xs font-semibold text-slate-600 uppercase">Seniority</p>
                </div>
                <p className="text-lg font-bold text-slate-800 capitalize">
                  {structured.seniority_level || sourcedData.seniority_level}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 flex justify-between items-center flex-shrink-0">
          <p className="text-xs text-slate-500">AI-powered analysis using Claude Haiku</p>
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-xl text-sm font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
